// src/lib/hooks/useMediaQuery.ts

/**
 * @fileoverview useMediaQuery hook
 * 
 * A React hook that tracks whether a CSS media query matches the current viewport.
 * Handles SSR gracefully by returning false on the server and syncing on hydration.
 * 
 * @param query - A valid CSS media query string (e.g., "(min-width: 768px)")
 * @returns boolean indicating whether the media query currently matches
 * 
 * @example
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 768px)')
 * const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
 * ```
 */

import { useState, useEffect, useRef, useCallback } from "react"

/**
 * Custom React hook to track the state of a CSS media query.
 *
 * - Returns `false` during SSR and before hydration.
 * - Handles both modern and legacy browsers (addEventListener/addListener).
 * - Cleans up listeners on unmount or query change.
 *
 * @param {string} query - A valid CSS media query string.
 * @returns {boolean} Whether the media query currently matches.
 */
export function useMediaQuery(query: string): boolean {
  // Validate query parameter
  if (typeof query !== "string" || query.trim() === "") {
    throw new TypeError("useMediaQuery: query must be a non-empty string")
  }

  /**
   * Returns whether the media query currently matches.
   * Returns false if not in a browser environment or if matchMedia is unavailable.
   */
  const getMatches = useCallback((mediaQuery: string): boolean => {
    // Server-side rendering safety check
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false
    }
    try {
      return window.matchMedia(mediaQuery).matches
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`useMediaQuery: Invalid media query "${mediaQuery}"`, error)
      }
      return false
    }
  }, [])

  // Store the current query in a ref to avoid stale closures in the event handler.
  // Sync in an effect (not during render) per react-hooks/refs; the ref is only read
  // inside the matchMedia listener below, which runs after this effect commits.
  const queryRef = useRef(query)
  useEffect(() => {
    queryRef.current = query
  }, [query])

  const [matches, setMatches] = useState<boolean>(() => getMatches(query))

  useEffect(() => {
    // Early return if not in browser environment
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return
    }

    let mediaQueryList: MediaQueryList
    
    try {
      mediaQueryList = window.matchMedia(query)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`useMediaQuery: Invalid media query "${query}"`, error)
      }
      return
    }

    // Use a stable handler reference for add/remove
    const handleChange = (event: MediaQueryListEvent | MediaQueryList): void => {
      // Some legacy browsers call with MediaQueryList, not event
      setMatches(typeof event === "object" && "matches" in event ? event.matches : mediaQueryList.matches)
    }

    // Set initial value in case the query changed
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time sync of the media-query match on mount (matchMedia is client-only); subsequent changes go through the listener above
    setMatches(mediaQueryList.matches)

    // Prefer addEventListener, fallback to addListener for legacy support
    let cleanup: (() => void) | undefined
    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange)
      cleanup = () => mediaQueryList.removeEventListener("change", handleChange)
    } else if (typeof mediaQueryList.addListener === "function") {
      mediaQueryList.addListener(handleChange)
      cleanup = () => mediaQueryList.removeListener(handleChange)
    }

    return cleanup
  }, [query, getMatches])

  return matches
}